import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { MdCloudOff } from "react-icons/md"
import { RiErrorWarningLine } from "react-icons/ri"
import { useStore } from "react-redux"
import { useProcessActions } from "~/hooks/useProcessActions"
import { VNZImageEntity } from "~/interfaces/entities/VNZImage"
import { vnzImagesSelectors } from "~/redux/state/VNZImages"
import { RootState } from "~/redux/store"
import CloudImageBrowserErrorScreen from "../../common/browser/CloudImageBrowserErrorScreen"
import { useCloudProviderActions } from "../../hooks/useCloudProviderActions"
import { useCloudProviderContext } from "../../hooks/useCloudProviderContext"
import { useVNZImageActions } from "../hooks/useVNZImageActions"
import { useVNZImages } from "../hooks/useVNZImages"
import VNZImageBrowserDesktop from "./VNZImageBrowserDesktop"
import VNZImageBrowserMobile from "./VNZImageBrowserMobile"

interface Props {
  isMobile: boolean
}

const VNZImageBrowser: React.FC<Props> = ({ isMobile }) => {
  const { t } = useTranslation()
  const store = useStore<RootState>()
  const { fetchVNZCloudImagePage } = useProcessActions()
  const { setIsImportingImages } = useCloudProviderContext()
  const { loadVNZFullImage, resetVNZImagesPagination } = useVNZImageActions()
  const { isLoading, hasLoadedImages, imageIds, hasError, hasNoImages } =
    useVNZImages()

  useEffect(() => {
    if (!hasLoadedImages) {
      fetchVNZCloudImagePage()
    }
  }, [hasLoadedImages, fetchVNZCloudImagePage])

  const {
    selectedImageIds,
    hasSelectedImages,
    isImportingImages,
    handleImageSelect,
    handleUploadImages,
    closeCloudProviderModal,
  } = useCloudProviderActions()

  const handleRetry = () => {
    // a terminal empty result sets hasMore to false; clear it so the fetch
    // guard lets the retry through
    resetVNZImagesPagination()
    fetchVNZCloudImagePage()
  }

  const BATCH_SIZE = 3

  const handleUploadImageEnhanced = async () => {
    setIsImportingImages(true)
    try {
      // Resolve the selected entities lazily from the store at click time, so
      // this component doesn't subscribe to the full entity list (which would
      // re-render it on every streaming thumbnail update).
      const state = store.getState()
      const selectedImages = selectedImageIds
        .map(id => vnzImagesSelectors.selectById(state, id))
        .filter((image): image is VNZImageEntity => Boolean(image))

      const selectedFiles: File[] = []

      for (let i = 0; i < selectedImages.length; i += BATCH_SIZE) {
        const batch = selectedImages.slice(i, i + BATCH_SIZE)
        const batchFiles = await Promise.all(
          batch.map(image => loadVNZFullImage(image))
        )
        selectedFiles.push(...batchFiles)
      }

      await handleUploadImages(selectedFiles)
    } catch (error) {
      console.error("Error uploading images from Verizon Cloud:", error)
      setIsImportingImages(false)
    }
  }

  if (hasError) {
    return (
      <CloudImageBrowserErrorScreen
        icon={RiErrorWarningLine}
        title={t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.ErrorScreen.Title"
        )}
        description={t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.ErrorScreen.Description"
        )}
        onRetry={handleRetry}
      />
    )
  }

  if (hasNoImages) {
    return (
      <CloudImageBrowserErrorScreen
        icon={MdCloudOff}
        title={t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.NoImagesScreen.Title"
        )}
        description={t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.NoImagesScreen.Description"
        )}
        onRetry={handleRetry}
      />
    )
  }

  if (isMobile) {
    return (
      <VNZImageBrowserMobile
        isLoading={isLoading}
        imageIds={imageIds}
        handleUploadImages={handleUploadImageEnhanced}
        hasSelectedImages={hasSelectedImages}
        isImportingImages={isImportingImages}
        handleImageSelect={handleImageSelect}
        selectedImageIds={selectedImageIds}
        onEndReached={fetchVNZCloudImagePage}
      />
    )
  }

  return (
    <VNZImageBrowserDesktop
      isLoading={isLoading}
      imageIds={imageIds}
      closeCloudProviderModal={closeCloudProviderModal}
      handleUploadImages={handleUploadImageEnhanced}
      hasSelectedImages={hasSelectedImages}
      isImportingImages={isImportingImages}
      handleImageSelect={handleImageSelect}
      selectedImageIds={selectedImageIds}
      onEndReached={fetchVNZCloudImagePage}
    />
  )
}

export default VNZImageBrowser
